import React, { useEffect, useMemo, useState } from 'react';
import api from '../../ui/api';
import useData from '../useData';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/PageShell';
import { Field, Select } from '../../ui/Form';
import { sectionOptions } from '../../ui/labels';

/**
 * «تفعيل القيد» — brings back a pupil who left (or was archived by
 * mistake). Used from الطلاب and from شؤون الطلاب, so both screens do
 * exactly the same thing.
 *
 * The office picks the section the pupil returns to; it starts on the
 * section they were last in when that one is still open. The server
 * does the rest in one call (POST /students/:id/reactivate): status,
 * a new enrollment from today, and the logins leaving had locked.
 */
export default function ReactivateStudentModal({ student, toast, onClose, onDone }) {
  const { data, loading } = useData(async () => {
    const [sections, enrollments] = await Promise.all([
      api.sections(),
      api.enrollments({ student_id: student.id }).catch(() => []),
    ]);
    return { sections, enrollments };
  }, [student.id]);

  const open = useMemo(
    () => (data?.sections || []).filter((s) => s.status === 'active')
      .sort((a, b) => (a.grade_order ?? 0) - (b.grade_order ?? 0)),
    [data]
  );
  // Newest first from the API: the first row is where the pupil was last.
  const lastSectionId = data?.enrollments?.[0]?.section_id;

  const [gradeId, setGradeId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [busy, setBusy] = useState(false);

  // Start on the pupil's last section, if it is still open.
  useEffect(() => {
    if (!data) return;
    const last = open.find((s) => s.id === lastSectionId);
    if (last) { setGradeId(String(last.grade_id)); setSectionId(String(last.id)); }
    else if (open.length) setGradeId(String(open[0].grade_id));
  }, [data, open, lastSectionId]);

  const grades = useMemo(() => [...new Map(open.map((s) => [
    String(s.grade_id), { value: String(s.grade_id), label: s.grade_title || '' },
  ])).values()], [open]);

  const submit = async () => {
    if (!sectionId) { toast.error('اختر الشعبة التي يعود إليها الطالب'); return; }
    setBusy(true);
    try {
      const r = await api.reactivateStudent(student.id, { section_id: Number(sectionId) });
      toast.success(`تم تفعيل قيد الطالب${r?.section_label ? ` في ${r.section_label}` : ''}`);
      onDone?.(r);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const leftOn = student.leave_date;
  const gapMonths = leftOn && leftOn.slice(0, 7) < new Date().toISOString().slice(0, 7);

  return (
    <Modal
      open
      size="sm"
      title={`تفعيل القيد: ${student.name}`}
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>إلغاء</Button>
          <Button variant="success" onClick={submit} loading={busy} disabled={loading || !sectionId}>
            تفعيل القيد
          </Button>
        </>
      )}
    >
      {(student.leave_reason || leftOn) && (
        <p className="ui-modal-message" style={{ marginBottom: 'var(--ui-space-3)' }}>
          أُلغي قيده{student.leave_reason ? <> بسبب: <strong>{student.leave_reason}</strong></> : null}
          {leftOn ? <> بتاريخ <bdi dir="ltr">{leftOn}</bdi></> : null}.
        </p>
      )}

      {!loading && open.length === 0 ? (
        <div className="ui-notice ui-notice-warning">
          <div className="ui-notice-body">لا توجد شعبة مفتوحة لتسجيل الطالب فيها.</div>
        </div>
      ) : (
        <>
          <Field label="الصف">
            <Select
              value={gradeId}
              onChange={(e) => { setGradeId(e.target.value); setSectionId(''); }}
              options={grades}
              disabled={loading}
            />
          </Field>
          <Field label="الشعبة" required hint="يُسجَّل فيها من اليوم.">
            <Select
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
              placeholder={loading ? 'جارٍ التحميل…' : 'اختر الشعبة'}
              options={sectionOptions(open.filter((s) => String(s.grade_id) === gradeId), { withCount: true })}
              disabled={loading}
            />
          </Field>
        </>
      )}

      <p className="ui-modal-message" style={{ marginTop: 'var(--ui-space-3)' }}>
        يعود الطالب نشطاً، وتُفتح حساباته وحسابات أهله التي أُغلقت عند إلغاء القيد.
        الباص لا يعود تلقائياً: عيّنه من «تعديل الطالب» إن لزم.
      </p>
      {gapMonths && (
        <div className="ui-notice ui-notice-warning" style={{ marginTop: 'var(--ui-space-3)' }}>
          <div className="ui-notice-body">
            الطالب منقطع منذ شهر سابق: الأقساط تُحسب من تاريخ التسجيل دون انقطاع، فراجع أقساطه بعد التفعيل.
          </div>
        </div>
      )}
    </Modal>
  );
}
